// Portal › Veri Girişi — /portal/kpi/tablo (M16 T19; mockup 15, DR1/DR4/DR6/DR8/DR11).
// KpiGrid `clientView` modunda: 🔒 readonly satırlar, `editable:false` hücreler salt okunur, "yıl
// durumu" kolonu (onay rozetleri + "danışman düzeltti"), formül popover'ında varyant talebi (K14)
// ve "danışman tarafından girilir" (DR11). Üstte amber açıklama bandı (DR6), pasif model notu
// ("Girdikçe kaydedilir…" — Tamamladım düğmesi YOK), sayaç "size açık kategorilerde…".
// Müşteri stale 409'da yalnız "sunucudaki değeri göster" (store `stale`). Boş durumlar: set erişimi
// yok / yalnız görüntüleme bandı / yüklenemedi + tekrar dene. "AI" ifadesi bu yüzeyde geçmez.
// P1-1: çok işletmeli müşteride PortalKpiBar işletme seçici; grid seçili işletmeye daraltılmış
// gelir (store), "Konsolide" seçiminde tablo salt okunur + bilgi bandı. P3-15: "Satıra git" hedef
// yılın hücresine kaydırır/odaklar (`highlight` + `hy` query). Mobil (≤640): dar dolgu.
// Düzen (2026-10-07): `<main>` ClientShell içerik alanının yüksekliğini alır
// (`calc(100vh - var(--layout-shell-header))`, 60 + 44 px alt sekme) ve flex sütun olur; kart
// kalan alanı doldurur, KpiGrid kabı kendi içinde kaydırır, alt bilgi satırı tablonun altında sabit.
// TODO-KPI-06: ≤1024 px'te dar dolgu (p-4) + dar arama kutusu; toolbar sarar, grid kendi kabında
// yatay kaydırır (sayfa taşmaz). Yapıştırma: tekil `saveCell` ardışık (portal store'a dokunulmadı).
// Kırılım (spec §6.5): aynı `buildRows` dört satır türü; toplam salt okunur, K3'te sunucu toplamı
// düşürüp grup bandını "<üst> — <grup>" etiketiyle taşır (istemci etiket kurmaz); üye kaydı
// sürerken toplam hücresi bekler (`pendingCells`); kategori süzgecine kırılım toplamı girmez.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { downloadPortalSetExcel } from "../../api/portal-kpi";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { EmptyState, LoadingState } from "../../components/common/StateSurface";
import { KpiExportButton } from "../../components/kpi/KpiExportButton";
import { KpiGrid } from "../../components/kpi/KpiGrid";
import { KpiFootnoteList } from "../../components/kpi/KpiFootnoteList";
import { KpiFootnoteContext, useGridFootnotes } from "../../lib/kpi-footnote-context";
import { KpiPageHeader } from "../../components/kpi/KpiPageHeader";
import { PortalKpiBar } from "../../components/kpi/PortalKpiBar";
import { useKpiNoteSummary } from "../../hooks/use-kpi-note-summary";
import { rootCategoryRows, type GridColumn } from "../../lib/kpi-grid-nav";
import { type GridNotes } from "../../lib/kpi-notes-ui";
import { formatKpiCell, KPI_COPY, readKpiQuery } from "../../lib/kpi-ui";
import { deepLinkTarget } from "../../lib/portal-deep-link";
import {
  collectClarifications,
  countClientCategories,
  portalFillCounterText,
  countClientFillStatus,
  PORTAL_CONSOLIDATED,
  portalTotalBand,
  portalTotalKind,
  withTotals,
} from "../../lib/portal-kpi-ui";
import { useMediaQuery } from "../../lib/use-media-query";
import { selectPortalTotalRecomputing, usePortalKpiStore } from "../../stores/portal-kpi-store";
import { usePortalStore } from "../../stores/portal-store";

const P = KPI_COPY.portal;

const btn: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.82rem",
  color: "var(--color-text)",
  minHeight: 34,
};

export function PortalKpiTablePage(): JSX.Element {
  const location = useLocation();
  const setsStatus = usePortalKpiStore((s) => s.setsStatus);
  const sets = usePortalKpiStore((s) => s.sets);
  const allSets = usePortalKpiStore((s) => s.allSets);
  const setId = usePortalKpiStore((s) => s.setId);
  const grid = usePortalKpiStore((s) => s.grid);
  const fullGrid = usePortalKpiStore((s) => s.fullGrid);
  const activeClientId = usePortalStore((s) => s.activeClientId);
  const access = usePortalKpiStore((s) => s.access);
  const cycle = usePortalKpiStore((s) => s.cycle);
  const error = usePortalKpiStore((s) => s.error);
  const gridLoading = usePortalKpiStore((s) => s.gridLoading);
  const years = usePortalKpiStore((s) => s.years);
  const saveErrors = usePortalKpiStore((s) => s.saveErrors);
  const lastSavedAt = usePortalKpiStore((s) => s.lastSavedAt);
  const pendingSaves = usePortalKpiStore((s) => s.pendingSaves);
  const stale = usePortalKpiStore((s) => s.stale);
  const unitChanged = usePortalKpiStore((s) => s.unitChanged);
  const pendingCells = usePortalKpiStore((s) => s.pendingCells);
  const entityId = usePortalKpiStore((s) => s.entityId);
  const entityChoice = usePortalKpiStore((s) => s.entityChoice);
  const entities = usePortalKpiStore((s) => s.entities);
  const tablet = useMediaQuery("(max-width: 1024px)");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const highlight = params.get("highlight");
  const highlightYearRaw = Number(params.get("hy"));
  const highlightYear =
    Number.isInteger(highlightYearRaw) && highlightYearRaw > 0 ? highlightYearRaw : null;
  const consolidated = entityChoice && entityId === PORTAL_CONSOLIDATED;
  const scopedEntityId = entityChoice && !consolidated ? entityId : undefined;
  const entityName = scopedEntityId ? entities.find((e) => e.id === scopedEntityId)?.name : null;

  // Bildirim linki `?setId=` → set seçimi (query temizlenir). Çok firmalı kullanıcıda set başka firmanın
  // ve erişilebilirse aktif firma ÖNCE o firmaya geçer (portal-store; set listesi yeniden yüklenince bu
  // efekt tekrar koşar ve seçimi tamamlar). Erişimsiz/bilinmeyen set sessizce atlanır — varlığı sızmaz.
  useEffect(() => {
    const q = readKpiQuery(location.search);
    if (!q.setId || setsStatus !== "ready") return;
    const link = deepLinkTarget({
      sets: allSets,
      activeClientId,
      setId: q.setId,
      highlight: null,
      he: null,
      hy: null,
      grid: null,
      // firması `firms`te olmayan set: geçiş yapılamaz → bağlantı aşağıda temizlenir (takılı kalmaz)
      firmIds: usePortalStore.getState().firms.map((f) => f.clientId),
    });
    if (link.switchClientId) {
      void usePortalStore.getState().setActiveClient(link.switchClientId);
      return;
    }
    if (sets.some((s) => s.id === q.setId) && q.setId !== setId) {
      void usePortalKpiStore.getState().selectSet(q.setId);
    }
    if (!sets.some((s) => s.id === q.setId)) {
      // erişimsiz: vurgu parametreleri de düşer
      params.delete("highlight");
      params.delete("he");
      params.delete("hy");
    }
    params.delete("setId");
    setParams(params, { replace: true });
  }, [location.search, setsStatus, sets, allSets, activeClientId, setId]);

  const set = sets.find((s) => s.id === setId);
  const totalKind = portalTotalKind(grid, set);
  // §12/1 — seçili işletme görünümünde toplam sütun grubu: yıl hücreleri işletmeden, toplam gerçek toplamdan
  const tableGrid = useMemo(
    () => (grid && fullGrid && scopedEntityId ? withTotals(grid, fullGrid) : grid),
    [grid, fullGrid, scopedEntityId],
  );
  // Derin bağlantı vurgusu: yalnız tabloda GERÇEKTEN görünen hedef (aksi sessizce yok)
  const heParam = params.get("he");
  const deepLink = useMemo(
    () =>
      params.get("setId")
        ? null
        : deepLinkTarget({
            sets: allSets,
            activeClientId,
            setId: null,
            highlight,
            he: heParam,
            hy: highlightYear,
            grid: tableGrid,
          }).highlight,
    [allSets, activeClientId, highlight, heParam, highlightYear, tableGrid, params],
  );
  useEffect(() => {
    // bildirimin işletmesi seçiliden farklıysa o işletmeye geç (hücre görünür olsun)
    if (deepLink?.entityId && entityChoice && deepLink.entityId !== entityId) {
      void usePortalKpiStore.getState().selectEntity(deepLink.entityId);
    }
  }, [deepLink?.entityId]);
  // kökteki kırılımlı toplam / K3 taşınmış grup bandı kategori değil (spec §6.1)
  const categories = useMemo(() => (grid ? rootCategoryRows(grid.indicators) : []), [grid]);
  const fill = useMemo(
    () => (grid ? countClientFillStatus(grid, scopedEntityId ?? undefined) : null),
    [grid, scopedEntityId],
  );
  const role = set?.role ?? "owner";
  const cats = useMemo(() => (grid ? countClientCategories(grid, role) : null), [grid, role]);
  const clarifications = useMemo(() => (grid ? collectClarifications(grid) : []), [grid]);
  const footnotes = useGridFootnotes(grid);
  const statusYear = years[years.length - 1] ?? new Date().getFullYear();

  useEffect(() => {
    if (!grid || initializedFor === grid.setId) return;
    setCollapsed(new Set()); // portalda bantlar açık — az kategori, doğrudan giriş
    setInitializedFor(grid.setId);
    setCategoryId(null);
  }, [grid, initializedFor]);

  // kural 12 — highlight hedefini kapsayacak şekilde filtreleri temizle
  useEffect(() => {
    if (!highlight || !grid) return;
    setSearch("");
    setCategoryId(null);
    const t = setTimeout(() => {
      params.delete("highlight");
      params.delete("hy");
      params.delete("he");
      setParams(params, { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [highlight, grid?.setId]);

  // Hücre notu rozetleri: görünür yıllar için özet, resolved isteğine paralel (grid'i beklemez)
  const noteSummary = useKpiNoteSummary("portal", setId, years, lastSavedAt);
  const notes: GridNotes | undefined = grid
    ? {
        audience: "portal",
        setId: grid.setId,
        summary: noteSummary.summary,
        onChanged: noteSummary.reload,
      }
    : undefined;

  // DR4 — üye kaydı sürerken toplam hücresi soluk + aria-busy (grid yenilenene kadar)
  const isRecomputing = useCallback(
    (id: string, col: GridColumn) => selectPortalTotalRecomputing(id, col)({ grid, pendingCells }),
    [grid, pendingCells],
  );

  const failedCount = Object.keys(saveErrors).length;
  const allOpen = collapsed.size === 0;
  // M17 — denetçi rolü set düzeyinde: tüm hücreler salt okunur, readonly bandının yerine denetçi bandı
  const auditor = role === "auditor";
  const readonlyOnly = !!access && access.open === 0;
  const gridReadOnly = auditor || readonlyOnly || consolidated;

  let body: React.ReactNode;
  // güvenlik/UX taraması 2026-10-08 (RK36): toolbar/durum metinleri KPI_COPY.grid tek kaynağından
  if (setsStatus === "idle" || setsStatus === "loading") {
    body = <LoadingState label={KPI_COPY.grid.loading} />;
  } else if (setsStatus === "error") {
    body = (
      <EmptyState
        title={P.loadFailed}
        action={
          <button
            type="button"
            style={btn}
            onClick={() => {
              const uid = usePortalKpiStore.getState().userId;
              if (uid) void usePortalKpiStore.getState().loadSets(uid, { force: true });
            }}
          >
            {P.retry}
          </button>
        }
      />
    );
  } else if (sets.length === 0) {
    body = <EmptyState title={P.noAccessTitle} hint={P.noAccessBody} />;
  } else if (error && !grid) {
    body = (
      <EmptyState
        title={P.loadFailed}
        hint={error}
        action={
          <button
            type="button"
            style={btn}
            onClick={() => void usePortalKpiStore.getState().refreshGrid()}
          >
            {P.retry}
          </button>
        }
      />
    );
  } else if (!grid || !fill) {
    body = <LoadingState label={KPI_COPY.grid.loading} />;
  } else {
    body = (
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {auditor ? (
          <div
            role="status"
            data-testid="portal-auditor-band"
            className="text-sm px-3 py-2 rounded-xl mb-3"
            style={{
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            <span aria-hidden="true">🔒</span> {P.auditorBand}
          </div>
        ) : readonlyOnly ? (
          <div
            role="status"
            data-testid="portal-readonly-band"
            className="text-sm px-3 py-2 rounded-xl mb-3"
            style={{
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            <span aria-hidden="true">🔒</span> {P.readonlyBand}
          </div>
        ) : null}
        {consolidated ? (
          <div
            role="status"
            data-testid="portal-consolidated-band"
            className="text-sm px-3 py-2 rounded-xl mb-3"
            style={{
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            <span aria-hidden="true">Σ</span> {portalTotalBand(totalKind)}
          </div>
        ) : null}
        {clarifications.length > 0 ? (
          <div
            role="alert"
            data-testid="portal-clarification-band"
            className="text-sm px-3 py-2 rounded-xl mb-3"
            style={{
              background: "var(--color-warning-bg)",
              border: "1px solid var(--color-warning-border)",
              color: "var(--color-warning-text)",
            }}
          >
            <b>{P.clarificationBand(clarifications.length)}</b>{" "}
            {clarifications.slice(0, 3).map((c, i) => (
              <span key={`${c.indicatorId}:${c.year}`}>
                {i > 0 ? " · " : ""}
                {c.label} · {c.year}
                {c.note ? ` — "${c.note}"` : ""}{" "}
                <button
                  type="button"
                  style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}
                  onClick={() => {
                    params.set("highlight", c.code);
                    params.set("hy", String(c.year));
                    setParams(params, { replace: true });
                  }}
                >
                  {P.goToRow}
                </button>
              </span>
            ))}
            {clarifications.length > 3 ? ` · +${clarifications.length - 3} daha` : ""}
          </div>
        ) : null}
        <div
          className="rounded-xl"
          data-testid="kpi-table-card"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            className="flex items-center gap-2 flex-wrap px-3 py-2"
            style={{ borderBottom: "1px solid var(--color-border)", flexShrink: 0 }}
          >
            <input
              type="search"
              aria-label={KPI_COPY.grid.search}
              placeholder={KPI_COPY.grid.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...btn, width: tablet ? 160 : 220 }}
            />
            <select
              aria-label={KPI_COPY.grid.categoryLabel}
              style={btn}
              value={categoryId ?? ""}
              onChange={(e) => setCategoryId(e.target.value || null)}
            >
              <option value="">{KPI_COPY.grid.categoryAll}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                color: "var(--color-primary)",
                fontWeight: 600,
              }}
              aria-live="polite"
              data-testid="portal-fill-counter"
            >
              {portalFillCounterText(fill, role)}
            </span>
            <button
              type="button"
              style={btn}
              onClick={() =>
                setCollapsed(
                  allOpen
                    ? new Set(
                        grid.indicators
                          .filter((i) => grid.indicators.some((c) => c.parentId === i.id))
                          .map((i) => i.id),
                      )
                    : new Set(),
                )
              }
            >
              {allOpen ? KPI_COPY.grid.collapseAll : KPI_COPY.grid.expandAll}
            </button>
            <span style={{ flex: 1 }} />
            {failedCount > 0 ? (
              // role=alert durum metninde, eylem ayrı düğmede (portal store yazılanı saklamaz →
              // yalnız sunucudaki değerleri yükle)
              <span className="inline-flex items-center gap-2 text-xs">
                <span role="alert" style={{ color: "var(--color-error)", fontWeight: 600 }}>
                  {KPI_COPY.grid.failedCells(failedCount)}
                </span>
                <button
                  type="button"
                  style={{ ...btn, borderColor: "var(--color-error)", color: "var(--color-error)" }}
                  onClick={() => void usePortalKpiStore.getState().refreshGrid()}
                >
                  {KPI_COPY.grid.loadServerValues}
                </button>
              </span>
            ) : pendingSaves > 0 ? (
              <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                {P.saving}
              </span>
            ) : lastSavedAt ? (
              <span
                className="text-xs"
                style={{ color: "var(--color-success)", fontWeight: 600 }}
                data-testid="portal-saved"
              >
                {P.savedPending}
              </span>
            ) : null}
            <KpiExportButton setId={grid.setId} years={years} onDownload={downloadPortalSetExcel} />
          </div>
          {cycle ? (
            <div
              role="alert"
              className="text-xs px-3 py-2"
              style={{
                background: "var(--color-warning-bg)",
                color: "var(--color-warning-text)",
                borderBottom: "1px solid var(--color-warning-border)",
              }}
            >
              {KPI_COPY.grid.cyclePortal(cycle.join(" → "))}
            </div>
          ) : null}
          {gridLoading ? (
            <div className="text-xs px-3 py-1" style={{ color: "var(--color-text-3)" }}>
              {KPI_COPY.grid.refreshing}
            </div>
          ) : null}
          <KpiFootnoteContext.Provider value={footnotes}>
            <KpiGrid
              grid={tableGrid ?? grid}
              mode="years"
              entityYear={statusYear}
              yearsEntityId={scopedEntityId ?? null}
              search={search}
              categoryId={categoryId}
              collapsed={collapsed}
              onToggleBand={(id) =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              onSaveCell={(args) => void usePortalKpiStore.getState().saveCell(args)}
              saveErrors={saveErrors}
              highlightCode={deepLink?.code ?? null}
              readOnly={gridReadOnly}
              onRefresh={() => void usePortalKpiStore.getState().refreshGrid()}
              notes={notes}
              breakdown={{ isRecomputing }}
              clientView={{
                setId: grid.setId,
                consolidated,
                highlightYear: deepLink?.year ?? null,
                totalColumns: true,
                unitChanged,
              }}
            />
          </KpiFootnoteContext.Provider>
          <KpiFootnoteList numbering={footnotes} />
          <div
            className="flex justify-between text-xs px-3 py-2 flex-wrap gap-2"
            style={{
              color: "var(--color-text-3)",
              borderTop: "1px solid var(--color-border)",
              flexShrink: 0,
            }}
          >
            <span>
              {cats ? P.footerEditable(cats.editable, cats.readonly) : ""}
              {entityName ? ` · ${P.entityHint(entityName)}` : ""}
            </span>
            <span>{P.verifiedWarn}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main
      className={tablet ? "p-4" : "p-8"}
      style={{
        fontFamily: "var(--font-sans)",
        maxWidth: 1600,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - var(--layout-shell-header, 0px))",
      }}
    >
      <KpiPageHeader
        title={KPI_COPY.titles.tablo}
        subtitle={set ? `${set.name} · ${P.passiveNote}` : P.passiveNote}
      />
      {setsStatus === "ready" && sets.length > 0 ? <PortalKpiBar showYears showEntity /> : null}
      {body}
      <ConfirmDialog
        open={stale !== null}
        title={P.staleTitle}
        body={
          stale
            ? `${P.staleBody}${
                stale.current
                  ? ` ${KPI_COPY.stale.serverValue(
                      formatKpiCell(
                        stale.current.numericValue !== null
                          ? {
                              status: "filled",
                              value: stale.current.numericValue,
                              isDerived: false,
                            }
                          : stale.current.textValue
                            ? { status: "filled", text: stale.current.textValue, isDerived: false }
                            : undefined,
                      ).text,
                    )}`
                  : ""
              }`
            : ""
        }
        confirmLabel={P.staleShow}
        tone="neutral"
        onConfirm={() => void usePortalKpiStore.getState().acknowledgeStale()}
        onCancel={() => void usePortalKpiStore.getState().acknowledgeStale()}
      />
    </main>
  );
}
